import type { Validators } from '@ps/web-shared-components';
import { Translation, required } from '@ps/web-shared-components';
import React from 'react';
import { useSelector } from 'react-redux';

import { select } from '@swf/core/store';
import { minLength, numbersOnly } from '@swf/core/utils/validators';

import accountImage from '../../assets/echeck-ca-account.png';
import branchImage from '../../assets/echeck-ca-branch.png';
import institutionImage from '../../assets/echeck-ca-institution.png';
import accountUsImage from '../../assets/echeck-us-account.png';
import bankRoutingUsImage from '../../assets/echeck-us-routing.png';
import type { CardType } from '../../types/cardType';
import InfoPrompt from '../InfoPrompt/InfoPrompt';
import PaymentMethodLogo from '../PaymentMethodLogo/PaymentMethodLogo';
import TextInput from '../TextInput/TextInput';

import {
  TXTCLI_Account_Number,
  TXTCLI_Please_enter_your_account_numb_ELL_1,
  TXTWCL_Bank_routing_number,
  TXTWCL_Branch_Transit,
  TXTWCL_Enter_your_9_digit_bank_routin_ELL,
  TXTWCL_Institution_Number,
  TXTWCL_Please_enter_N0_digit_number,
  TXTWCL_Please_enter_account_number,
  TXTWCL_Whole_numbers_onlyX_No_decimal_ELL
} from './BankFields.messages.json';
import { image, labelGroup } from './BankFields.scss';

interface Validator {
  accountType?: Validators;
  accountNumber?: Validators;
  institution?: Validators;
  branch?: Validators;
}

interface Props {
  fieldName: string;
  validators?: Validator;
  cardType?: CardType;
  helperMessage?: React.ReactNode;
}

interface BankFieldsProps extends React.FunctionComponent<Props> {
  BankAccount: typeof BankAccount;
  BankBranch: typeof BankBranch;
  BankInstitution: typeof BankInstitution;
  BankRouting: typeof BankRouting;
}

const getFieldValidation = (fieldLength: number) => [
  required({ ...TXTWCL_Please_enter_N0_digit_number, values: { 0: fieldLength } }),
  numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL),
  minLength(fieldLength, { ...TXTWCL_Please_enter_N0_digit_number, values: { 0: fieldLength } })
];

export const BankAccount: React.FC<Props> = ({
  fieldName,
  validators,
  cardType,
  helperMessage
}) => {
  const isSiteUS = useSelector(select.cms.isSiteUS);

  return (
    <TextInput
      id={fieldName}
      type="tel"
      label={TXTCLI_Account_Number}
      labelContent={
        <InfoPrompt title={{ translation: TXTCLI_Account_Number }} variant="deposit">
          <img src={isSiteUS ? accountUsImage : accountImage} className={image} alt="" />
        </InfoPrompt>
      }
      helperMessage={helperMessage}
      className={labelGroup}
      maxLength={16}
      validators={[
        required(TXTWCL_Please_enter_account_number),
        numbersOnly(TXTWCL_Whole_numbers_onlyX_No_decimal_ELL),
        ...(validators?.accountNumber || [])
      ]}
      {...(cardType && {
        startAdornment: 'logo',
        icon: <PaymentMethodLogo cardType={cardType} variant="small" />
      })}
    />
  );
};

const BankRouting: React.FC<Props> = ({ fieldName, validators, helperMessage }) => (
  <TextInput
    id={fieldName}
    type="tel"
    label={TXTWCL_Bank_routing_number}
    placeholder={'000000000'}
    helperMessage={helperMessage}
    labelContent={
      <InfoPrompt title={{ translation: TXTWCL_Bank_routing_number }} variant="deposit">
        <img src={bankRoutingUsImage} className={image} alt="" />
      </InfoPrompt>
    }
    className={labelGroup}
    maxLength={9}
    validators={[...getFieldValidation(9), ...(validators?.branch || [])]}
  />
);

const BankInstitution: React.FC<Props> = ({ fieldName, validators }) => (
  <TextInput
    id={fieldName}
    type="tel"
    label={TXTWCL_Institution_Number}
    placeholder={'000'}
    labelContent={
      <InfoPrompt title={{ translation: TXTWCL_Institution_Number }} variant="deposit">
        <img src={institutionImage} className={image} alt="" />
      </InfoPrompt>
    }
    className={labelGroup}
    maxLength={3}
    validators={[...getFieldValidation(3), ...(validators?.institution || [])]}
  />
);

const BankBranch: React.FC<Props> = ({ fieldName, validators }) => (
  <TextInput
    id={fieldName}
    type="tel"
    label={TXTWCL_Branch_Transit}
    placeholder={'00000'}
    labelContent={
      <InfoPrompt title={{ translation: TXTWCL_Branch_Transit }} variant="deposit">
        <img src={branchImage} className={image} alt="" />
      </InfoPrompt>
    }
    className={labelGroup}
    maxLength={5}
    validators={[...getFieldValidation(5), ...(validators?.branch || [])]}
  />
);

const BankFieldsUS: React.FC<Props> = ({ fieldName, validators, cardType }) => (
  <>
    <BankAccount
      fieldName={`${fieldName}.accountNumber`}
      validators={validators}
      cardType={cardType}
      helperMessage={<Translation {...TXTCLI_Please_enter_your_account_numb_ELL_1} />}
    />
    <BankRouting
      fieldName={`${fieldName}.branch`}
      validators={validators}
      helperMessage={<Translation {...TXTWCL_Enter_your_9_digit_bank_routin_ELL} />}
    />
  </>
);

const BankFields: BankFieldsProps = ({ fieldName, validators, cardType }) => {
  const isSiteUS = useSelector(select.cms.isSiteUS);
  return isSiteUS ? (
    <BankFieldsUS fieldName={fieldName} validators={validators} cardType={cardType} />
  ) : (
    <>
      <BankAccount
        fieldName={`${fieldName}.accountNumber`}
        validators={validators}
        cardType={cardType}
      />
      <BankInstitution fieldName={`${fieldName}.institution`} validators={validators} />
      <BankBranch fieldName={`${fieldName}.branch`} validators={validators} />
    </>
  );
};

BankFields.BankAccount = BankAccount;
BankFields.BankInstitution = BankInstitution;
BankFields.BankBranch = BankBranch;
BankFields.BankRouting = BankRouting;

export default BankFields;
